import React, { useEffect, useMemo, useState } from "react"
import { Box, Paper, Stack, Typography } from "@mui/material"
import CodeViewer from "../../../../components/CodeViewer/code-viewer"
import InputAutoComplete from "../../../../components/Input/Input-auto-complete"

const AutocompleteInputPage = () => {
    const [companyValue, setCompanyValue] = useState(null)
    const [companyInput, setCompanyInput] = useState("")

    const [asyncValue, setAsyncValue] = useState(null)
    const [asyncInput, setAsyncInput] = useState("")
    const [asyncOptions, setAsyncOptions] = useState([])
    const [asyncLoading, setAsyncLoading] = useState(true)

    const [perDigitValue, setPerDigitValue] = useState(null)
    const [perDigitInput, setPerDigitInput] = useState("")
    const [perDigitOptions, setPerDigitOptions] = useState([])
    const [perDigitLoading, setPerDigitLoading] = useState(false)

    const [formValue, setFormValue] = useState(null)
    const [formInput, setFormInput] = useState("")

    const companyOptions = useMemo(() => ([
        { label: "BTG Pactual Pharma Participações S.A.", value: "13068817000143", cpf_cnpj: "13.068.817/0001-43" },
        { label: "XP Investimentos S.A.", value: "02421421000106", cpf_cnpj: "02.421.421/0001-06" },
        { label: "ACME Serviços Financeiros", value: "11222333000144", cpf_cnpj: "11.222.333/0001-44" },
        { label: "João da Silva", value: "22233344455", cpf_cnpj: "222.333.444-55" },
    ]), [])

    useEffect(() => {
        const timer = setTimeout(() => {
            setAsyncOptions(companyOptions.slice(0, 2))
            setAsyncLoading(false)
        }, 900)

        return () => clearTimeout(timer)
    }, [])

    useEffect(() => {
        if (!perDigitInput) {
            setPerDigitOptions([])
            setPerDigitLoading(false)
            return
        }

        setPerDigitLoading(true)

        const timer = setTimeout(() => {
            const normalized = perDigitInput.toLowerCase()
            const filtered = companyOptions.filter((opt) => {
                const label = opt.label?.toLowerCase() || ""
                const doc = (opt.cpf_cnpj || opt.value || "").toString().toLowerCase()
                return label.includes(normalized) || doc.includes(normalized)
            })

            // Simula resposta do servidor por dígito
            setPerDigitOptions(filtered)
            setPerDigitLoading(false)
        }, 450)

        return () => clearTimeout(timer)
    }, [companyOptions, perDigitInput])

    const codeBasicUsage = `import InputAutoComplete from "src/v2/components/Input/Input-auto-complete"

const options = [
  { label: "BTG Pactual Pharma Participações S.A.", value: "13068817000143", cpf_cnpj: "13.068.817/0001-43" },
  { label: "XP Investimentos S.A.", value: "02421421000106", cpf_cnpj: "02.421.421/0001-06" },
]

const [value, setValue] = useState(null)
const [inputValue, setInputValue] = useState("")

<InputAutoComplete
  label="Empresa"
  value={value}
  inputValue={inputValue}
  options={options}
  filterKeys={["label", "cpf_cnpj"]}
  onInputChange={(event, newInput) => setInputValue(newInput)}
  onChange={(event, option) => {
    setValue(option?.value || null)
    setInputValue(option ? \`\${option.label} \${option.cpf_cnpj ?? ""}\`.trim() : "")
  }}
  noOptionsText={(input) => input ? \`Não encontrado \${input}\` : "Nenhum resultado"}
/>`

    const codeFormVariant = `import InputAutoComplete from "src/v2/components/Input/Input-auto-complete"

const [value, setValue] = useState(null)
const [inputValue, setInputValue] = useState("")

<InputAutoComplete
  label="Grupo econômico"
  value={value}
  inputValue={inputValue}
  options={options}
  inputVariant="form"
  freeSolo
  filterKeys={["label", "cpf_cnpj", "value"]}
  onInputChange={(event, newInput) => setInputValue(newInput)}
  onChange={(event, option) => {
    setValue(option?.value || option || null)
    setInputValue(option?.label || option || "")
  }}
  noOptionsText="Nenhum grupo econômico encontrado"
/>`

    const codePropsType = `type AutoCompleteOption = {
  label?: string
  value?: string | number
  cpf_cnpj?: string
  [key: string]: any
}

type InputAutoCompleteProps = {
  label: string
  value?: any
  inputValue?: string
  options?: AutoCompleteOption[]
  loading?: boolean
  inputVariant?: "default" | "form"        // "default" para filtros/tabelas e "form" para modais/formulários (filled)
  freeSolo?: boolean                       // permite digitação livre ou seleção da lista
  filterKeys?: string[]                    // chaves usadas para filtrar localmente
  normalizeFilter?: (val: string) => string
  minCharsToFilter?: number
  noOptionsText?: string | ((input: string) => string)
  getOptionLabel?: (option: AutoCompleteOption) => string
  isOptionEqualToValue?: (option: AutoCompleteOption, value: any) => boolean
  renderOption?: (props: any, option: AutoCompleteOption) => React.ReactNode
  getOptionDisabled?: (option: AutoCompleteOption) => boolean
  onChange?: (event: any, option: AutoCompleteOption | null) => void
  onInputChange?: (event: any, value: string) => void
  startIcon?: React.ReactElement
  endIcon?: React.ReactElement
  onEndIconClick?: () => void
  error?: boolean
  helperText?: string
  labelLeft?: number
  // ...props adicionais são repassados ao Autocomplete do MUI
}`

    const codePerDigit = `<InputAutoComplete
  label="Buscar por dígito"
  value={value}
  inputValue={inputValue}
  options={optionsFromServer}
  loading={loading}
  filterKeys={["label", "cpf_cnpj"]}
  minCharsToFilter={2}
  onInputChange={(event, newInput) => {
    setInputValue(newInput)
    debounceFetch(newInput) // dispara request a cada digitação
  }}
  onChange={(event, option) => {
    setValue(option?.value || null)
    setInputValue(option ? \`\${option.label} \${option.cpf_cnpj ?? ""}\`.trim() : "")
  }}
  noOptionsText={(input) => input ? \`Não encontrado \${input}\` : "Nenhum resultado"}
/>`

    return (
        <Box sx={{ width: "100%" }}>
            <Stack spacing={3}>

                <Paper>
                    <Box p={3}>
                        <Typography variant="h4" gutterBottom color="common.white">
                            {`InputAutoComplete`}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            {`Autocomplete pronto para uso com filtros por múltiplas chaves, personalização do texto de "não encontrado", estado de loading e suporte a ícones.`}
                        </Typography>

                        <Box mt={3}>
                            <Typography variant="h6" gutterBottom color="common.white">
                                {`Busca local (razão social / documento)`}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" paragraph>
                                {`Busca local controlada filtrando por razão social ou CPF/CNPJ. O valor salvo é o documento, enquanto o campo exibe label + documento.`}
                            </Typography>

                            <Box mt={2} sx={{ maxWidth: 520 }}>
                                <InputAutoComplete
                                    label="Empresa"
                                    value={companyValue}
                                    inputValue={companyInput}
                                    options={companyOptions}
                                    filterKeys={["label", "cpf_cnpj", "value"]}
                                    onInputChange={(event, newInput) => setCompanyInput(newInput)}
                                    onChange={(event, option) => {
                                        setCompanyValue(option?.value || null)
                                        setCompanyInput(option ? `${option.label} ${option.cpf_cnpj ?? ""}`.trim() : "")
                                    }}
                                    noOptionsText={(input) => input ? `Não encontrado ${input}` : "Nenhum resultado"}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Paper>

                <Paper>
                    <Box p={3}>
                        <Typography variant="h6" gutterBottom color="common.white">
                            {`Com loading inicial`}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" paragraph>
                            {`Exemplo simulando carregamento assíncrono: enquanto a lista não chega, o componente exibe spinner e mantém o estado como warning.`}
                        </Typography>

                        <Box mt={2} sx={{ maxWidth: 520 }}>
                            <InputAutoComplete
                                label="Empresa (assíncrono)"
                                value={asyncValue}
                                inputValue={asyncInput}
                                options={asyncOptions}
                                loading={asyncLoading}
                                filterKeys={["label", "cpf_cnpj"]}
                                onInputChange={(event, newInput) => setAsyncInput(newInput)}
                                onChange={(event, option) => {
                                    setAsyncValue(option?.value || null)
                                    setAsyncInput(option ? `${option.label} ${option.cpf_cnpj ?? ""}`.trim() : "")
                                }}
                                noOptionsText={(input) => input ? `Não encontrado ${input}` : "Nenhum resultado"}
                            />
                        </Box>
                    </Box>
                </Paper>

                <Paper>
                    <Box p={3}>
                        <Typography variant="h6" gutterBottom color="common.white">
                            {`Request a cada dígito (simulado)`}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" paragraph>
                            {`Exemplo semelhante ao fluxo de CETIP/ISIN: a cada digitação disparamos uma busca remota. Aqui o carregamento é simulado com setTimeout para ilustrar o comportamento.`}
                        </Typography>

                        <Box mt={2} sx={{ maxWidth: 520 }}>
                            <InputAutoComplete
                                label="Buscar ativo"
                                value={perDigitValue}
                                inputValue={perDigitInput}
                                options={perDigitOptions}
                                loading={perDigitLoading}
                                filterKeys={["label", "cpf_cnpj", "value"]}
                                minCharsToFilter={2}
                                onInputChange={(event, newInput) => setPerDigitInput(newInput)}
                                onChange={(event, option) => {
                                    setPerDigitValue(option?.value || null)
                                    setPerDigitInput(option ? `${option.label} ${option.cpf_cnpj ?? ""}`.trim() : "")
                                }}
                                noOptionsText={(input) => input ? `Não encontrado ${input}` : "Nenhum resultado"}
                            />
                        </Box>

                        <Box mt={3}>
                            <Paper variant="outlined">
                                <Box p={2}>
                                    <Typography variant="subtitle2" gutterBottom color="common.white">
                                        {`Exemplo de request por dígito`}
                                    </Typography>
                                    <CodeViewer code={codePerDigit} language="jsx" />
                                </Box>
                            </Paper>
                        </Box>
                    </Box>
                </Paper>

                <Paper>
                    <Box p={3}>
                        <Typography variant="h6" gutterBottom color="common.white">
                            {`Variante para Formulários e Modais (inputVariant="form")`}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" paragraph>
                            {`Visual padronizado com os demais inputs de formulário (variant="filled"), ideal para modais de cadastro e edição. Suporta freeSolo para digitação livre.`}
                        </Typography>

                        <Box mt={2} sx={{ maxWidth: 520 }}>
                            <InputAutoComplete
                                label="Grupo econômico"
                                value={formValue}
                                inputValue={formInput}
                                options={companyOptions}
                                inputVariant="form"
                                freeSolo
                                filterKeys={["label", "cpf_cnpj", "value"]}
                                onInputChange={(event, newInput) => setFormInput(newInput)}
                                onChange={(event, option) => {
                                    setFormValue(option?.value || option || null)
                                    setFormInput(option?.label || option || "")
                                }}
                                noOptionsText="Nenhum grupo econômico encontrado"
                            />
                        </Box>

                        <Box mt={3}>
                            <Paper variant="outlined">
                                <Box p={2}>
                                    <Typography variant="subtitle2" gutterBottom color="common.white">
                                        {`Exemplo de uso em formulários`}
                                    </Typography>
                                    <CodeViewer code={codeFormVariant} language="jsx" />
                                </Box>
                            </Paper>
                        </Box>
                    </Box>
                </Paper>

                <Paper>
                    <Box p={3}>
                        <Typography variant="h6" gutterBottom color="common.white">
                            {`Uso básico`}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" paragraph>
                            {`Configuração mínima com lista de opções, controle de value/inputValue e filtro por múltiplas chaves.`}
                        </Typography>

                        <Paper variant="outlined">
                            <Box p={2}>
                                <CodeViewer code={codeBasicUsage} language="jsx" />
                            </Box>
                        </Paper>
                    </Box>
                </Paper>

                <Paper>
                    <Box p={3}>
                        <Typography variant="h6" gutterBottom color="common.white">
                            {`Propriedades do componente`}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" paragraph>
                            {`Principais props expostas pelo InputAutoComplete. Props extras são repassadas para o Autocomplete do MUI.`}
                        </Typography>

                        <Paper variant="outlined">
                            <Box p={2}>
                                <Typography variant="subtitle2" gutterBottom color="common.white">
                                    {`Tipo das props (exemplo em TypeScript`}
                                    {`)`}
                                </Typography>
                                <CodeViewer code={codePropsType} language="tsx" />
                            </Box>
                        </Paper>
                    </Box>
                </Paper>

            </Stack>
        </Box>
    )
}

export default AutocompleteInputPage
